import { isNil } from 'lodash'
import isFinite from 'lodash/isFinite'
import isNumber from 'lodash/isNumber'
import toNumber from 'lodash/toNumber'

export const convertToStringWithSeparator = (
	number: Key | null,
	separator: string = ','
): string => {
	if (!number && number !== 0) {
		return ''
	}
	const str = number.toString()
	const parts = str.split('.')
	const intPart = parts[0]
	const decPart = parts[1]
	const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
	return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt
}

export const numberToPercentage = (number: number | null, fractionDigits: number = 2) =>
	number ? (number * 100).toFixed(fractionDigits) + '%' : null

const formatInteger = (value: string | null, total: number, allowNegative: boolean) => {
	if (!value || total < 0) {
		return ''
	}

	const val = allowNegative ? value.replace(/([^0-9-]+)/, '') : value.replace(/([^0-9]+)/, '')
	const regExp = allowNegative ? `(-?\\d{0,${total}})` : `(\\d{0,${total}})`
	const [, match1] = new RegExp(regExp, 'g').exec(val) || ['', '']

	return match1
}

// Number(5,2) means:
// 5 - maximum number of digits in number <-- total
// 2 - maximum number of digits after dot <-- afterDot
export const formatDecimal = (
	value: string | null,
	total: number,
	afterDot: number,
	allowNegative: boolean = false
) => {
	if (!value || total < 0 || afterDot < 0) {
		return ''
	}

	const replacedZeroValue = value.replace(/^(-?)0+(?=\d)/, '$1')
	if (afterDot === 0) {
		return formatInteger(replacedZeroValue, total, allowNegative)
	} else {
		const val = allowNegative
			? replacedZeroValue.replace(/([^0-9-.]+)/, '')
			: replacedZeroValue.replace(/([^0-9.]+)/, '')
		const isInteger = !val.includes('.')
		if (isInteger) {
			return formatInteger(replacedZeroValue, total - afterDot, allowNegative)
		} else {
			const regExp = allowNegative
				? `(-?\\d{0,${total - afterDot}})[^.]*((?:\\.\\d{0,${afterDot}})?)`
				: `(\\d{0,${total - afterDot}})[^.]*((?:\\.\\d{0,${afterDot}})?)`
			const [, match1, match2] = new RegExp(regExp, 'g').exec(val) || ['', '', '']
			return `${match1}${match2}`
		}
	}
}

export const removeSpacesFromInputValue = (value: string) => parseInt(value.replace(/ /g, ''))

export const convertInputValueToStringWithSpaceSeparator = (value: string) => {
	const formatted = value.replace(/([^0-9])/gi, '')
	return convertToStringWithSeparator(formatted, ' ')
}

export const fixedNumOfDecimals = (value: number, digits: number = 2) => value.toFixed(digits)

export const parseFloatNumberStringToFloatNumber = (floatNumber: string | null) => {
	if (!floatNumber) {
		return null
	}
	const parsedNumber = parseFloat(floatNumber)
	if (isNumber(parsedNumber)) {
		return parsedNumber
	}
	return null
}

export const parseStringToNullableNumber = (input: string | null): number | null => {
  if (isNil(input) || input === '') return null
  const num = toNumber(input)
  return isFinite(num) ? num : null
}

export const multiplyFloatStrings = ({
	a,
	b,
	afterDot,
	maxResult
}: {
	a: string | null
	b: string | null
	afterDot: number
	maxResult: number
}): number => {
	let newValue: number
	const aNumber = parseFloat(a || '')
	const bNumber = parseFloat(b || '')
	if (isFinite(aNumber) && isFinite(bNumber)) {
		const multipliedNumber = parseFloat((aNumber * bNumber).toFixed(afterDot))
		newValue = multipliedNumber < maxResult ? multipliedNumber : maxResult
	} else {
		newValue = 0
	}
	return newValue
}

export const parseStringId = (value: string | null) => {
	const parsedValue = parseInt(value || '')
	return isNaN(parsedValue) ? null : parsedValue
}

export const isOnlyNumber = (query?: string) => {
	if (query) {
		return /^-?\d+$/.test(query)
	}
}

export const inRange = (numberToCheck: number, min: number, max: number) =>
	numberToCheck >= min && numberToCheck <= max

export const checkRange = (
	lowValue: BasicString | undefined,
	highValue: BasicString | undefined
) => {
	if (!lowValue || !highValue) {
		return true
	}
	const parsedLowValue = parseFloat(lowValue)
	const parsedHighValue = parseFloat(highValue)
	if (isFinite(parsedLowValue) && isFinite(parsedHighValue)) {
		return parsedHighValue >= parsedLowValue
	}
	return true
}

/**
 * Check overlap
 * @return {boolean} Returns true if not overlap
 */
export const checkOverlap = (
	lowValue: BasicString | undefined,
	highValue: BasicString | undefined,
	ranges: {
		rangeMin: BasicNumber
		rangeMax: BasicNumber
	}[]
) => {
	if (!lowValue || !highValue) {
		return true
	}
	const parsedLowValue = parseFloat(lowValue)
	const parsedHighValue = parseFloat(highValue)
	if (isFinite(parsedLowValue) && isFinite(parsedHighValue)) {
		return !ranges.some((range) => {
			const currentMinRange = range.rangeMin as number
			const currentMaxRange = range.rangeMax as number
			const isSelectedRangeInCurrent =
				inRange(parsedLowValue, currentMinRange, currentMaxRange) ||
				inRange(parsedHighValue, currentMinRange, currentMaxRange)

			const isCurrentRangeInSelected =
				inRange(currentMinRange, parsedLowValue, parsedHighValue) ||
				inRange(currentMaxRange, parsedLowValue, parsedHighValue)
			return isSelectedRangeInCurrent || isCurrentRangeInSelected
		})
	}
	return true
}

export const isValueZero = <T extends Key | Nullish | object | boolean>(value: T): boolean =>
	isNil(value) ||
	value === 0 ||
	(typeof value === 'string' && value.trim() === '') ||
	value === '0' ||
	value === '0.0' ||
	value === '0.00' ||
	value === '0.000'

export const getArrayIndexByName = (name: string) => toNumber(name.replace(/\D/g, ''))

export const uniqueNumber = () => Math.random() * Date.now()
